import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Save, Plus, Trash2, GripVertical, ShieldCheck, Eye, Play,
  MessageSquare, ListChecks, FileUp, PenTool, Info, ClipboardCheck, BadgeCheck,
  Loader2, X, AlertTriangle, CheckCircle2, XCircle, ArrowRight, ChevronDown,
} from "lucide-react";
import AppShell from "../components/app/AppShell";
import PageHeader, { StatusPill } from "../components/app/PageHeader";
import api, { formatApiError } from "../lib/api";
import { track, EVT } from "../lib/analytics";

const STEP_TYPES = [
  { type: "welcome", label: "Welcome message", icon: MessageSquare, desc: "Greeting and introduction." },
  { type: "questionnaire", label: "Questionnaire", icon: ListChecks, desc: "Ask the client questions." },
  { type: "document_request", label: "Document request", icon: FileUp, desc: "Request files from the client." },
  { type: "e_signature", label: "E-signature", icon: PenTool, desc: "Get a legally binding signature." },
  { type: "information", label: "Information screen", icon: Info, desc: "Display content for the client to read." },
  { type: "consent", label: "Consent collection", icon: ClipboardCheck, desc: "Collect explicit consents." },
  { type: "identity_verification", label: "Identity verification", icon: BadgeCheck, desc: "Verify client identity." },
];

function StepTypePicker({ onPick, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative card2 max-w-3xl w-full p-6 animate-slide-up" data-testid="step-picker">
        <div className="flex items-center justify-between mb-5">
          <h3 className="t-h3">Add a step</h3>
          <button onClick={onClose} className="p-2 text-slate2 hover:text-ink"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {STEP_TYPES.map((s) => (
            <button key={s.type} className="card2 card2-hover p-4 text-left" onClick={() => onPick(s.type)} data-testid={`picker-${s.type}`}>
              <s.icon size={22} className="text-indigo2 mb-3" />
              <div className="t-h4">{s.label}</div>
              <p className="t-caption mt-1">{s.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function StepEditor({ step, onChange }) {
  const cfg = step.config || {};
  const set = (key, val) => onChange({ ...step, config: { ...cfg, [key]: val } });

  if (step.type === "welcome" || step.type === "information") {
    return (
      <>
        <label className="label2">Heading</label>
        <input className="input2 mb-3" value={cfg.heading || ""} onChange={(e) => set("heading", e.target.value)} maxLength={80} />
        <label className="label2">Body</label>
        <textarea className="textarea2 mb-3" rows={6} value={cfg.body || ""} onChange={(e) => set("body", e.target.value)} />
        {step.type === "welcome" && (
          <>
            <label className="label2">Button label</label>
            <input className="input2" value={cfg.button_label || ""} onChange={(e) => set("button_label", e.target.value)} placeholder="Begin Onboarding" />
          </>
        )}
        {step.type === "information" && (
          <label className="flex items-center gap-2 mt-3 t-body-sm">
            <input type="checkbox" checked={cfg.ack_required || false} onChange={(e) => set("ack_required", e.target.checked)} className="w-4 h-4 accent-indigo2" />
            Require "I understand" acknowledgement
          </label>
        )}
      </>
    );
  }

  if (step.type === "questionnaire") {
    const qs = cfg.questions || [];
    const addQ = () => set("questions", [...qs, { id: Math.random().toString(36).slice(2), text: "", type: "short_text", required: false }]);
    return (
      <>
        <label className="label2">Intro text</label>
        <textarea className="textarea2 mb-4" rows={2} value={cfg.intro || ""} onChange={(e) => set("intro", e.target.value)} />
        <label className="flex items-center gap-2 mb-4 t-body-sm">
          <input type="checkbox" checked={cfg.one_at_a_time || false} onChange={(e) => set("one_at_a_time", e.target.checked)} className="w-4 h-4 accent-indigo2" />
          Show one question per screen (higher completion rates)
        </label>
        <div className="t-label text-slate2 mb-2">Questions</div>
        <div className="space-y-3">
          {qs.map((q, i) => (
            <div key={q.id} className="border border-border2 rounded-md p-3">
              <div className="flex items-start gap-2">
                <span className="t-mono t-caption mt-2 text-slate2">{i + 1}.</span>
                <div className="flex-1 space-y-2">
                  <input className="input2" placeholder="Question text" value={q.text} onChange={(e) => { const c = [...qs]; c[i] = { ...q, text: e.target.value }; set("questions", c); }} />
                  <div className="flex gap-2">
                    <select className="input2 flex-1" value={q.type} onChange={(e) => { const c = [...qs]; c[i] = { ...q, type: e.target.value }; set("questions", c); }}>
                      <option value="short_text">Short text</option>
                      <option value="long_text">Long text</option>
                      <option value="single_choice">Multiple choice (single)</option>
                      <option value="multi_choice">Checkboxes (multi)</option>
                      <option value="yes_no">Yes / No</option>
                      <option value="date">Date</option>
                      <option value="number">Number</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="scale">Scale 1-10</option>
                      <option value="file">File upload</option>
                    </select>
                    <label className="flex items-center gap-2 t-caption">
                      <input type="checkbox" checked={q.required} onChange={(e) => { const c = [...qs]; c[i] = { ...q, required: e.target.checked }; set("questions", c); }} className="w-4 h-4 accent-indigo2" />Required
                    </label>
                  </div>
                </div>
                <button onClick={() => set("questions", qs.filter((_, j) => j !== i))} className="text-slate2 hover:text-rose2 p-2"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
        <button onClick={addQ} className="btn btn-secondary btn-sm mt-3" data-testid="add-question"><Plus size={14} />Add question</button>
      </>
    );
  }

  if (step.type === "document_request") {
    const reqs = cfg.requests || [];
    return (
      <>
        <label className="label2">Instructions</label>
        <textarea className="textarea2 mb-4" rows={2} value={cfg.intro || ""} onChange={(e) => set("intro", e.target.value)} />
        <div className="t-label text-slate2 mb-2">Documents requested</div>
        <div className="space-y-2">
          {reqs.map((r, i) => (
            <div key={i} className="flex gap-2 items-center border border-border2 rounded-md p-3">
              <input className="input2 flex-1" placeholder="Document name (e.g. Photo ID)" value={r.name} onChange={(e) => { const c = [...reqs]; c[i] = { ...r, name: e.target.value }; set("requests", c); }} />
              <label className="flex items-center gap-2 t-caption">
                <input type="checkbox" checked={r.required !== false} onChange={(e) => { const c = [...reqs]; c[i] = { ...r, required: e.target.checked }; set("requests", c); }} className="w-4 h-4 accent-indigo2" />Required
              </label>
              <button onClick={() => set("requests", reqs.filter((_, j) => j !== i))} className="text-slate2 hover:text-rose2"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <button onClick={() => set("requests", [...reqs, { name: "", required: true, formats: ["pdf", "jpg", "png"] }])} className="btn btn-secondary btn-sm mt-3">+ Add document</button>
      </>
    );
  }

  if (step.type === "consent") {
    const items = cfg.items || [];
    return (
      <>
        <label className="label2">Intro</label>
        <textarea className="textarea2 mb-4" rows={2} value={cfg.intro || ""} onChange={(e) => set("intro", e.target.value)} />
        <div className="t-label text-slate2 mb-2">Consent items</div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2 items-center border border-border2 rounded-md p-3">
              <input className="input2 flex-1" placeholder="Consent statement" value={it.text} onChange={(e) => { const c = [...items]; c[i] = { ...it, text: e.target.value }; set("items", c); }} />
              <label className="flex items-center gap-2 t-caption">
                <input type="checkbox" checked={it.required !== false} onChange={(e) => { const c = [...items]; c[i] = { ...it, required: e.target.checked }; set("items", c); }} className="w-4 h-4 accent-indigo2" />Required
              </label>
              <button onClick={() => set("items", items.filter((_, j) => j !== i))} className="text-slate2 hover:text-rose2"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <button onClick={() => set("items", [...items, { text: "", required: true }])} className="btn btn-secondary btn-sm mt-3">+ Add consent</button>
      </>
    );
  }

  if (step.type === "e_signature") {
    return (
      <>
        <label className="label2">Document name</label>
        <input className="input2 mb-3" value={cfg.document_name || ""} onChange={(e) => set("document_name", e.target.value)} />
        <label className="label2">Instructions to client</label>
        <textarea className="textarea2 mb-3" rows={3} value={cfg.instructions || ""} onChange={(e) => set("instructions", e.target.value)} />
        <label className="label2">Signature method</label>
        <select className="input2 mb-3" value={cfg.signature_method || "both"} onChange={(e) => set("signature_method", e.target.value)}>
          <option value="both">Draw or type</option>
          <option value="draw">Draw only</option>
          <option value="type">Type only</option>
        </select>
        <label className="flex items-center gap-2 mb-2 t-body-sm">
          <input type="checkbox" checked={cfg.scroll_to_sign !== false} onChange={(e) => set("scroll_to_sign", e.target.checked)} className="w-4 h-4 accent-indigo2" />
          Require client to scroll to bottom before signing
        </label>
      </>
    );
  }

  if (step.type === "identity_verification") {
    return (
      <>
        <label className="label2">Instructions</label>
        <textarea className="textarea2 mb-3" rows={3} value={cfg.instructions || "Please upload a photo ID and a recent proof of address."} onChange={(e) => set("instructions", e.target.value)} />
        <div className="security-card p-3 t-body-sm">
          <ShieldCheck size={16} className="inline mr-1 text-emerald2-dark" />
          Documents are encrypted at rest with AES-256 and stored UK-only.
        </div>
      </>
    );
  }

  return null;
}

function ScanResults({ scan, onApplyFix, onClose }) {
  if (!scan) return null;
  const VerdictBadge = ({ v }) => v === "pass" ? <span className="badge2 badge-verified">PASS</span> : v === "review" ? <span className="badge2 badge-pending">REVIEW</span> : <span className="badge2 badge-failed">FAIL</span>;
  const blocking = scan.flagged_items.filter((it) => it.severity === "critical").length;
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative max-w-2xl mx-auto my-10 card2 p-6 animate-slide-up">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="t-h2 mb-1">Compliance scan</h3>
            <div className="flex items-center gap-2"><VerdictBadge v={scan.verdict} /><span className="t-mono t-h3">{scan.score}/100</span></div>
          </div>
          <button onClick={onClose} className="p-2 text-slate2 hover:text-ink"><X size={20} /></button>
        </div>
        <p className="t-body text-slate2 mb-5">{scan.summary}</p>

        {blocking > 0 && (
          <div className="security-card p-3 mb-4" style={{ borderLeftColor: "#EF4444", background: "rgba(239,68,68,0.04)" }}>
            <strong className="text-rose2">{blocking} critical issue(s)</strong> must be addressed before activating this flow.
          </div>
        )}

        {scan.flagged_items.length > 0 && (
          <>
            <div className="t-label text-slate2 mb-3">Flagged items</div>
            <div className="space-y-3 mb-5">
              {scan.flagged_items.map((it, i) => (
                <div key={i} className="border-l-3 rounded-md border border-border2 p-4" style={{ borderLeftColor: it.severity === "critical" ? "#EF4444" : it.severity === "warning" ? "#F59E0B" : "#4F46E5", borderLeftWidth: 3 }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`badge2 ${it.severity === "critical" ? "badge-failed" : it.severity === "warning" ? "badge-pending" : "badge-locked"}`}>{it.severity}</span>
                    <span className="t-mono t-caption">{it.rule_reference}</span>
                  </div>
                  <p className="italic t-body-sm mb-2" style={{ color: "#475569" }}>"{it.excerpt}"</p>
                  <p className="t-body-sm mb-2">{it.explanation}</p>
                  <p className="t-body-sm" style={{ color: "#065F46" }}><strong>Suggested:</strong> {it.suggested_fix}</p>
                </div>
              ))}
            </div>
          </>
        )}
        {scan.passed_checks?.length > 0 && (
          <details className="mb-3">
            <summary className="t-body-sm cursor-pointer text-emerald2-dark">✓ {scan.passed_checks.length} passed checks</summary>
            <ul className="mt-2 space-y-1">
              {scan.passed_checks.map((c, i) => <li key={i} className="t-body-sm pl-4">• {c}</li>)}
            </ul>
          </details>
        )}
        <button className="btn btn-secondary btn-block" onClick={onClose}>Close and return to editor</button>
      </div>
    </div>
  );
}

export default function FlowBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [flow, setFlow] = useState(null);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/api/flows/${id}`);
        setFlow(data);
        if (data.steps?.[0]) setSelectedStepId(data.steps[0].id);
      } catch (e) {
        alert(formatApiError(e));
        nav("/app/flows");
      }
    })();
  }, [id, nav]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/api/flows/${id}`, { name: flow.name, description: flow.description, steps: flow.steps, settings: flow.settings });
      setFlow(data); setDirty(false);
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const runScan = async () => {
    setScanning(true);
    if (dirty) await save();
    try {
      const { data } = await api.post(`/api/flows/${id}/scan`, { ruleset: "all" });
      setScanResult(data);
      // Refresh flow to update last_scan
      const { data: refreshed } = await api.get(`/api/flows/${id}`);
      setFlow(refreshed);
    } catch (e) { alert(formatApiError(e)); }
    finally { setScanning(false); }
  };

  const activate = async () => {
    try {
      await api.post(`/api/flows/${id}/activate`);
      track(EVT.flow_activated, { flow_id: id });
      const { data } = await api.get(`/api/flows/${id}`);
      setFlow(data);
      alert("Flow activated!");
    } catch (e) { alert(formatApiError(e)); }
  };

  const addStep = (type) => {
    const newStep = { id: Math.random().toString(36).slice(2, 14), type, name: STEP_TYPES.find((t) => t.type === type).label, config: {} };
    setFlow({ ...flow, steps: [...flow.steps, newStep] });
    setSelectedStepId(newStep.id);
    setPicker(false);
    setDirty(true);
  };

  const updateStep = (updated) => {
    setFlow({ ...flow, steps: flow.steps.map((s) => s.id === updated.id ? updated : s) });
    setDirty(true);
  };

  const removeStep = (sid) => {
    setFlow({ ...flow, steps: flow.steps.filter((s) => s.id !== sid) });
    if (selectedStepId === sid) setSelectedStepId(flow.steps[0]?.id || null);
    setDirty(true);
  };

  const moveStep = (idx, dir) => {
    const ns = [...flow.steps];
    const target = idx + dir;
    if (target < 0 || target >= ns.length) return;
    [ns[idx], ns[target]] = [ns[target], ns[idx]];
    setFlow({ ...flow, steps: ns });
    setDirty(true);
  };

  if (!flow) return <AppShell><div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-indigo2" /></div></AppShell>;

  const selectedStep = flow.steps.find((s) => s.id === selectedStepId);
  const lastScan = flow.last_scan;

  return (
    <AppShell>
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <Link to="/app/flows" className="btn btn-ghost btn-sm"><ArrowLeft size={16} />Back</Link>
        <input
          className="t-h2 px-2 py-1 -ml-2 border border-transparent hover:border-border2 rounded-md flex-1 min-w-0 max-w-md"
          value={flow.name}
          onChange={(e) => { setFlow({ ...flow, name: e.target.value }); setDirty(true); }}
          data-testid="flow-name-input"
        />
        <StatusPill status={flow.status} />
        {lastScan && (
          <span className={`badge2 ${lastScan.verdict === "pass" ? "badge-verified" : lastScan.verdict === "review" ? "badge-pending" : "badge-failed"}`}>
            Score {lastScan.score}/100
          </span>
        )}
        <div className="flex-1" />
        <button className="btn btn-secondary btn-sm" onClick={save} disabled={saving} data-testid="save-flow">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
        <button className="btn btn-secondary btn-sm" onClick={runScan} disabled={scanning || flow.steps.length === 0} data-testid="scan-flow">
          {scanning ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Scan
        </button>
        <button className="btn btn-primary btn-sm" onClick={activate} disabled={!lastScan || lastScan.verdict === "fail" || flow.status === "active"} data-testid="activate-flow">
          <Play size={14} /> {flow.status === "active" ? "Active" : "Activate"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_320px] gap-4">
        {/* Step list */}
        <div className="card2 p-3" data-testid="step-list">
          <div className="t-label text-slate2 px-2 py-2">Steps ({flow.steps.length})</div>
          <div className="space-y-1">
            {flow.steps.map((s, idx) => {
              const TypeIcon = STEP_TYPES.find((t) => t.type === s.type)?.icon || MessageSquare;
              return (
                <div
                  key={s.id}
                  className={`group rounded-md px-2 py-2 cursor-pointer flex items-center gap-2 ${selectedStepId === s.id ? "bg-indigo2-50 border border-indigo2/30" : "hover:bg-surface"}`}
                  onClick={() => setSelectedStepId(s.id)}
                  data-testid={`step-${idx}`}
                >
                  <span className="t-mono t-caption w-5 text-slate2">{idx + 1}</span>
                  <TypeIcon size={16} className="text-indigo2 flex-shrink-0" />
                  <span className="t-body-sm flex-1 truncate">{s.name || "Untitled"}</span>
                  <div className="opacity-0 group-hover:opacity-100 flex">
                    <button onClick={(e) => { e.stopPropagation(); moveStep(idx, -1); }} className="p-1 text-slate2 hover:text-ink" disabled={idx === 0}><ChevronDown size={14} className="rotate-180" /></button>
                    <button onClick={(e) => { e.stopPropagation(); removeStep(s.id); }} className="p-1 text-slate2 hover:text-rose2"><Trash2 size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={() => setPicker(true)} className="btn btn-secondary btn-block btn-sm mt-3" data-testid="add-step"><Plus size={14} />Add step</button>
        </div>

        {/* Editor */}
        <div className="card2 p-6 min-h-[400px]" data-testid="step-editor">
          {selectedStep ? (
            <>
              <label className="label2">Step name</label>
              <input className="input2 mb-4" value={selectedStep.name} onChange={(e) => updateStep({ ...selectedStep, name: e.target.value })} />
              <StepEditor step={selectedStep} onChange={updateStep} />
            </>
          ) : (
            <div className="text-center py-16">
              <p className="t-body text-slate2">Add your first step to get started.</p>
            </div>
          )}
        </div>

        {/* Live preview */}
        <div className="card2 p-4 bg-surface" data-testid="step-preview">
          <div className="t-label text-slate2 mb-3 flex items-center gap-2"><Eye size={14} /> Client preview</div>
          <div className="bg-white rounded-md border border-border2 overflow-hidden">
            <div className="px-4 py-3 bg-navy-900 text-white">
              <div className="t-h4">{flow.firm_id ? "Your firm" : "Firm"}</div>
            </div>
            <div className="p-5 min-h-[280px]">
              {selectedStep ? (
                <>
                  <div className="t-h3 mb-2">{selectedStep.config?.heading || selectedStep.name}</div>
                  <p className="t-body-sm text-slate2 whitespace-pre-wrap">{selectedStep.config?.body || selectedStep.config?.intro || selectedStep.config?.instructions || "—"}</p>
                  {selectedStep.type === "questionnaire" && (selectedStep.config?.questions || []).slice(0, 3).map((q, i) => (
                    <div key={i} className="mt-4">
                      <label className="label2">{q.text || `Question ${i + 1}`}{q.required && <span className="req">*</span>}</label>
                      <input className="input2" disabled placeholder="Client's answer" />
                    </div>
                  ))}
                  {selectedStep.type === "document_request" && (selectedStep.config?.requests || []).map((r, i) => (
                    <div key={i} className="mt-3 p-3 border border-dashed border-border2 rounded-md text-center">
                      <FileUp size={20} className="mx-auto mb-1 text-slate2" />
                      <div className="t-body-sm">{r.name}</div>
                    </div>
                  ))}
                  {selectedStep.type === "consent" && (selectedStep.config?.items || []).map((it, i) => (
                    <label key={i} className="flex items-start gap-2 mt-3 t-body-sm">
                      <input type="checkbox" disabled className="w-4 h-4 mt-0.5" />{it.text}
                    </label>
                  ))}
                </>
              ) : <p className="t-caption">Select a step.</p>}
            </div>
          </div>
        </div>
      </div>

      {picker && <StepTypePicker onPick={addStep} onClose={() => setPicker(false)} />}
      {scanResult && <ScanResults scan={scanResult} onClose={() => setScanResult(null)} />}
    </AppShell>
  );
}
